// src/components/family/profile/DiagnosisSummaryCard.tsx
// Diagnosis summary card for child profile overview

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  ChevronRight,
  Info,
  Calendar,
  Star,
  ExternalLink,
  BookOpen,
} from 'lucide-react';
import { format } from 'date-fns';
import type { ChildDiagnosis } from '@/types';

interface DiagnosisSummaryCardProps {
  diagnoses: ChildDiagnosis[];
  childId: string;
  className?: string;
}

export const DiagnosisSummaryCard: React.FC<DiagnosisSummaryCardProps> = ({
  diagnoses,
  childId,
  className = '',
}) => {
  // Separate primary from secondary diagnoses
  const primaryDiagnosis = diagnoses.find((d) => d.isPrimary);
  const secondaryDiagnoses = diagnoses.filter((d) => !d.isPrimary);

  if (diagnoses.length === 0) {
    return (
      <div className={`bg-white rounded-2xl border border-cream-dark p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
            <Heart className="w-5 h-5 text-sage" />
            Diagnoses
          </h3>
        </div>

        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-cream-dark flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-warm-gray" />
          </div>
          <p className="text-navy font-medium mb-1">No diagnoses recorded</p>
          <p className="text-sm text-warm-gray mb-4">
            Add your child's diagnoses to access tailored resources
          </p>
          <Link
            to={`/family/children/${childId}/edit#diagnoses`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium text-sm hover:bg-sage-dark transition-colors"
          >
            Add Diagnosis
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl border border-cream-dark overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-cream-dark">
        <h3 className="text-lg font-display font-semibold text-navy flex items-center gap-2">
          <Heart className="w-5 h-5 text-sage" />
          Diagnoses
          <span className="text-sm font-normal text-warm-gray">
            ({diagnoses.length})
          </span>
        </h3>
        <Link
          to={`/family/children/${childId}#diagnoses`}
          className="text-sm text-sage font-medium hover:text-sage-dark flex items-center gap-1 transition-colors"
        >
          Manage
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Primary diagnosis */}
      {primaryDiagnosis && (
        <div className="p-4 bg-gradient-to-r from-sage/5 to-transparent border-b border-cream-dark">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-sage/10 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-sage" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-semibold text-navy truncate">
                  {primaryDiagnosis.diagnosis?.name || 'Primary Diagnosis'}
                </h4>
                <span className="px-2 py-0.5 bg-sage/10 text-sage-dark text-xs font-medium rounded-full flex-shrink-0">
                  Primary
                </span>
              </div>

              {primaryDiagnosis.diagnosis?.shortName && (
                <p className="text-sm text-warm-gray mb-2">
                  {primaryDiagnosis.diagnosis.shortName}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-warm-gray">
                {primaryDiagnosis.diagnosedDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Diagnosed {format(new Date(primaryDiagnosis.diagnosedDate), 'MMM yyyy')}
                  </span>
                )}
                {primaryDiagnosis.severity && (
                  <span className="flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" />
                    {primaryDiagnosis.severity}
                  </span>
                )}
              </div>

              {/* Quick links */}
              {primaryDiagnosis.diagnosis?.slug && (
                <div className="mt-3 flex gap-2">
                  <Link
                    to={`/resources/diagnoses/${primaryDiagnosis.diagnosis.slug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-cream-dark rounded-lg text-xs font-medium text-navy hover:border-sage/30 transition-colors"
                  >
                    <BookOpen className="w-3.5 h-3.5 text-sage" />
                    View Resources
                  </Link>
                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(primaryDiagnosis.diagnosis.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-cream-dark rounded-lg text-xs font-medium text-navy hover:border-sage/30 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5 text-navy-light" />
                    Learn More
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Secondary diagnoses */}
      {secondaryDiagnoses.length > 0 && (
        <div className="divide-y divide-cream-dark">
          {secondaryDiagnoses.slice(0, 3).map((diagnosis) => (
            <div
              key={diagnosis.id}
              className="p-4 hover:bg-cream/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-cream-dark flex items-center justify-center flex-shrink-0">
                  <Heart className="w-4 h-4 text-warm-gray" />
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-navy truncate">
                    {diagnosis.diagnosis?.name || 'Diagnosis'}
                  </h4>
                  <div className="flex items-center gap-3 text-xs text-warm-gray mt-0.5">
                    {diagnosis.diagnosedDate && (
                      <span>
                        {format(new Date(diagnosis.diagnosedDate), 'MMM yyyy')}
                      </span>
                    )}
                    {diagnosis.subtype && (
                      <span className="truncate">{diagnosis.subtype}</span>
                    )}
                  </div>
                </div>

                <ChevronRight className="w-4 h-4 text-warm-gray flex-shrink-0" />
              </div>
            </div>
          ))}

          {/* Show more link if there are additional diagnoses */}
          {secondaryDiagnoses.length > 3 && (
            <Link
              to={`/family/children/${childId}#diagnoses`}
              className="block p-3 text-center text-sm text-sage font-medium hover:bg-cream/50 transition-colors"
            >
              View {secondaryDiagnoses.length - 3} more diagnosis
              {secondaryDiagnoses.length - 3 !== 1 ? 'es' : ''}
            </Link>
          )}
        </div>
      )}

      {/* Empty secondary diagnoses state */}
      {primaryDiagnosis && secondaryDiagnoses.length === 0 && (
        <div className="p-4 text-center text-sm text-warm-gray">
          <p>No additional diagnoses recorded</p>
        </div>
      )}
    </div>
  );
};

// Loading skeleton
export const DiagnosisSummaryCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl border border-cream-dark overflow-hidden animate-pulse">
    <div className="flex items-center justify-between p-4 border-b border-cream-dark">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-cream-dark rounded" />
        <div className="h-6 bg-cream-dark rounded w-24" />
      </div>
      <div className="h-4 bg-cream-dark rounded w-16" />
    </div>

    <div className="p-4 bg-gradient-to-r from-cream/50 to-transparent">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-cream-dark" />
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-cream-dark rounded w-40" />
          <div className="h-4 bg-cream-dark rounded w-32" />
          <div className="h-3 bg-cream-dark rounded w-24" />
        </div>
      </div>
    </div>

    {[1, 2].map((i) => (
      <div key={i} className="p-4 border-t border-cream-dark">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cream-dark" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-cream-dark rounded w-32" />
            <div className="h-3 bg-cream-dark rounded w-20" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default DiagnosisSummaryCard;
